/**
 * ЕДИНСТВЕННОЕ место, где цена переворачивается.
 *
 * Всё, что приходит из наших ценовых источников, уже приведено к
 * `market_quote за market_base` — binance.bid/ask, uniswap.execution_price,
 * our_price. Нормализация сделана на стороне бота. Инвертировать не надо.
 *
 * Инверсия нужна ровно в двух случаях:
 *
 *  1. Обратная нога пары. Direction — не биржевая пара: base = что мейкер
 *     ПОЛУЧАЕТ, quote = что ПЛАТИТ, цена = quote_amount / base_amount.
 *     Поэтому '1:WETH->USDC' даёт 1881.47, а '1:USDC->WETH' даёт 0.00053074 —
 *     шкалы взаимно обратны, общей оси у них нет по построению.
 *
 *  2. Цена филла: она в ориентации НАПРАВЛЕНИЯ, не рынка. Положенная как есть
 *     на ось «USDC за 1 ETH» она улетит за экран.
 *
 * ЗАПРЕЩЕНО угадывать ориентацию по величине числа. В старом дашборде стояла
 * эвристика `1/v4 > 100 ? 1/v4 : v4` с уже неверным комментарием: она
 * фактически не срабатывала, но перевернула бы корректную цену на любой паре,
 * где честная цена меньше 0.01. Ориентация выводится из типа, а не из значения.
 */
import type { Asset, DirectionKey, Market } from '../types'

export interface ParsedDirection {
  chainId: number
  /** мейкер получает */
  base: Asset
  /** мейкер платит */
  quote: Asset
}

const ARROW = /^(\d+):([A-Z]+)\s*(?:->|→)\s*([A-Z]+)$/

export function parseDirection(key: DirectionKey): ParsedDirection {
  const m = ARROW.exec(key.trim())
  if (!m) throw new Error(`не разобрать направление: ${key}`)
  return { chainId: Number(m[1]), base: m[2] as Asset, quote: m[3] as Asset }
}

/** Рынок, на графике которого живёт это направление. */
export function marketOf(key: DirectionKey): Market {
  const { base, quote } = parseDirection(key)
  const set = new Set<string>([base, quote])
  if (set.has('WETH') && set.has('USDC')) return 'ETH-USDC'
  if (set.has('WETH') && set.has('USDT')) return 'ETH-USDT'
  return 'USDC-USDT'
}

/** Является ли направление обратной ногой относительно ориентации рынка. */
export function isReverseLeg(key: DirectionKey): boolean {
  const { base } = parseDirection(key)
  const market = marketOf(key)
  // Рынок котируется как quote за base: ETH-USDC → USDC за WETH, USDC-USDT →
  // USDT за USDC. Нога «прямая», если мейкер получает базовый актив рынка.
  const marketBase: Asset = market === 'USDC-USDT' ? 'USDC' : 'WETH'
  return base !== marketBase
}

/**
 * Цена направления, приведённая к ориентации рынка. Единственный вызов
 * инверсии во всём приложении.
 */
export function toMarketOrientation(key: DirectionKey, price: number): number {
  return isReverseLeg(key) ? 1 / price : price
}

/**
 * Произведение цен двух ног пары. > 1 означает, что книга скрещена и венью
 * режет ПАРУ, а не одно направление. Считается из живых цен, не хардкодится.
 */
export function pairProduct(forwardPrice: number, reversePrice: number): number {
  return forwardPrice * reversePrice
}

/** Запас до скрещивания в bps. Отрицательный = уже скрещено. */
export function crossHeadroomBps(product: number): number {
  return (1 - product) * 10_000
}
